import { useEffect, useState } from "react";

const LOGO_URL = "https://static.readdy.ai/image/e50d925263add9c47b947a5709171c8c/d6ffeb12f52f4a37bedad9b6dbef1af6.png";

export interface GlobalLoadingScreenProps {
  duration?: number;
  onComplete?: () => void;
  visible?: boolean;
  minDuration?: number;
}

export default function GlobalLoadingScreen({
  duration = 0,
  onComplete,
  visible = true,
  minDuration = 1200,
}: GlobalLoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!visible) {
      setIsExiting(true);
      const timer = setTimeout(() => {
        onComplete?.();
      }, 800);
      return () => clearTimeout(timer);
    }

    setIsExiting(false);
    setIsReady(false);
    setProgress(0);

    const start = Date.now();
    let raf: number;

    const animate = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / minDuration) * 100, 95);
      setProgress(pct);

      if (elapsed >= minDuration) {
        setIsReady(true);
      } else {
        raf = requestAnimationFrame(animate);
      }
    };

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [visible, minDuration, onComplete]);

  useEffect(() => {
    if (isReady && duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onComplete?.(), 800);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isReady, duration, onComplete]);

  if (!visible && !isExiting) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none ${
        isExiting ? "animate-[loadingExit_0.8s_ease-out_forwards]" : ""
      }`}
      aria-live="polite"
      aria-busy={!isReady}
    >
      {/* Sin fondo, sin blur, sin color — 100% transparente */}

      <div className="relative z-10 flex flex-col items-center justify-center">
        <div
          className="relative flex items-center justify-center"
          style={{ width: 200, height: 200 }}
        >
          {/* Aro luminoso giratorio exterior */}
          <svg
            width={200}
            height={200}
            viewBox="0 0 200 200"
            className="absolute inset-0 animate-[ringRotate_5s_linear_infinite]"
          >
            <defs>
              <filter id="ringGlow">
                <feGaussianBlur stdDeviation="2.5" result="blur" />
                <feMerge>
                  <feMergeNode in="blur" />
                  <feMergeNode in="SourceGraphic" />
                </feMerge>
              </filter>
              <linearGradient
                id="ringGrad"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#06B6D4" stopOpacity="0" />
                <stop offset="15%" stopColor="#06B6D4" stopOpacity="0.55" />
                <stop offset="45%" stopColor="#22D3EE" stopOpacity="0.7" />
                <stop offset="55%" stopColor="#22D3EE" stopOpacity="0.7" />
                <stop offset="85%" stopColor="#06B6D4" stopOpacity="0.55" />
                <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle
              cx={100}
              cy={100}
              r={92}
              fill="none"
              stroke="url(#ringGrad)"
              strokeWidth={1.2}
              filter="url(#ringGlow)"
            />
          </svg>

          {/* Aro interior más sutil, girando en reversa */}
          <svg
            width={200}
            height={200}
            viewBox="0 0 200 200"
            className="absolute inset-0 animate-[ringRotateReverse_7s_linear_infinite]"
          >
            <circle
              cx={100}
              cy={100}
              r={80}
              fill="none"
              stroke="rgba(56,189,248,0.25)"
              strokeWidth={0.6}
              strokeDasharray="40 280"
              strokeLinecap="round"
            />
          </svg>

          {/* Rayo láser — glow difuso de fondo */}
          <div
            className="absolute overflow-hidden"
            style={{ width: 160, height: 80, marginTop: 4 }}
          >
            <div
              className="absolute top-1/2 w-full"
              style={{ transform: "translateY(-50%)" }}
            >
              <div
                className="h-[5px] w-40 mx-auto animate-[laserSweep_3s_ease-in-out_infinite]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(6,182,212,0.05) 10%, rgba(34,211,238,0.35) 40%, rgba(6,182,212,0.35) 60%, rgba(6,182,212,0.05) 90%, transparent 100%)",
                  filter: "blur(4px)",
                }}
              />
            </div>
          </div>

          {/* Rayo láser — línea principal pronunciada */}
          <div
            className="absolute overflow-hidden"
            style={{ width: 160, height: 80, marginTop: 4 }}
          >
            <div
              className="absolute top-1/2 w-full"
              style={{ transform: "translateY(-50%)" }}
            >
              <div
                className="h-[3px] w-36 mx-auto animate-[laserSweep_3s_ease-in-out_infinite]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.2) 12%, rgba(34,211,238,1) 40%, rgba(6,182,212,1) 60%, rgba(34,211,238,0.2) 88%, transparent 100%)",
                  boxShadow:
                    "0 0 8px rgba(34,211,238,0.7), 0 0 18px rgba(34,211,238,0.4), 0 0 32px rgba(6,182,212,0.25)",
                }}
              />
            </div>
          </div>

          {/* Logo RFW original */}
          <img
            src={LOGO_URL}
            alt="RFW"
            className="relative z-10 animate-[logoBreath_3.5s_ease-in-out_infinite]"
            style={{ width: 110, height: 110, objectFit: "contain" }}
          />
        </div>

        <span className="mt-6 text-xs tracking-[0.35em] uppercase text-white/35 font-medium">
          RadioFrecuenciaWeb
        </span>
      </div>
    </div>
  );
}

export function GlobalLoadingScreenLight({
  duration = 0,
  onComplete,
  visible = true,
  minDuration = 1200,
}: GlobalLoadingScreenProps) {
  const [progress, setProgress] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (!visible) {
      setIsExiting(true);
      const timer = setTimeout(() => {
        onComplete?.();
      }, 800);
      return () => clearTimeout(timer);
    }

    setIsExiting(false);
    setIsReady(false);
    setProgress(0);

    const start = Date.now();
    let raf: number;

    const animate = () => {
      const elapsed = Date.now() - start;
      const pct = Math.min((elapsed / minDuration) * 100, 95);
      setProgress(pct);

      if (elapsed >= minDuration) {
        setIsReady(true);
      } else {
        raf = requestAnimationFrame(animate);
      }
    };

    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [visible, minDuration, onComplete]);

  useEffect(() => {
    if (isReady && duration > 0) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => onComplete?.(), 800);
      }, duration);
      return () => clearTimeout(timer);
    }
  }, [isReady, duration, onComplete]);

  if (!visible && !isExiting) return null;

  return (
    <div
      className={`fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none ${
        isExiting ? "animate-[loadingExit_0.8s_ease-out_forwards]" : ""
      }`}
      aria-live="polite"
      aria-busy={!isReady}
    >
      {/* Sin fondo, sin blur, sin color — 100% transparente */}

      <div className="relative z-10 flex flex-col items-center justify-center">
        <div
          className="relative flex items-center justify-center"
          style={{ width: 200, height: 200 }}
        >
          {/* Aro luminoso giratorio */}
          <svg
            width={200}
            height={200}
            viewBox="0 0 200 200"
            className="absolute inset-0 animate-[ringRotate_5s_linear_infinite]"
          >
            <defs>
              <linearGradient
                id="ringGradLight"
                x1="0%"
                y1="0%"
                x2="100%"
                y2="100%"
              >
                <stop offset="0%" stopColor="#06B6D4" stopOpacity="0" />
                <stop offset="15%" stopColor="#06B6D4" stopOpacity="0.45" />
                <stop offset="45%" stopColor="#22D3EE" stopOpacity="0.55" />
                <stop offset="55%" stopColor="#22D3EE" stopOpacity="0.55" />
                <stop offset="85%" stopColor="#06B6D4" stopOpacity="0.45" />
                <stop offset="100%" stopColor="#06B6D4" stopOpacity="0" />
              </linearGradient>
            </defs>
            <circle
              cx={100}
              cy={100}
              r={92}
              fill="none"
              stroke="url(#ringGradLight)"
              strokeWidth={1}
            />
          </svg>

          {/* Rayo láser — glow difuso de fondo */}
          <div
            className="absolute overflow-hidden"
            style={{ width: 160, height: 80, marginTop: 4 }}
          >
            <div
              className="absolute top-1/2 w-full"
              style={{ transform: "translateY(-50%)" }}
            >
              <div
                className="h-[5px] w-40 mx-auto animate-[laserSweep_3s_ease-in-out_infinite]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(6,182,212,0.05) 10%, rgba(34,211,238,0.35) 40%, rgba(6,182,212,0.35) 60%, rgba(6,182,212,0.05) 90%, transparent 100%)",
                  filter: "blur(4px)",
                }}
              />
            </div>
          </div>

          {/* Rayo láser — línea principal pronunciada */}
          <div
            className="absolute overflow-hidden"
            style={{ width: 160, height: 80, marginTop: 4 }}
          >
            <div
              className="absolute top-1/2 w-full"
              style={{ transform: "translateY(-50%)" }}
            >
              <div
                className="h-[3px] w-36 mx-auto animate-[laserSweep_3s_ease-in-out_infinite]"
                style={{
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.2) 12%, rgba(34,211,238,1) 40%, rgba(6,182,212,1) 60%, rgba(34,211,238,0.2) 88%, transparent 100%)",
                  boxShadow:
                    "0 0 8px rgba(34,211,238,0.7), 0 0 18px rgba(34,211,238,0.4), 0 0 32px rgba(6,182,212,0.25)",
                }}
              />
            </div>
          </div>

          {/* Logo RFW original */}
          <img
            src={LOGO_URL}
            alt="RFW"
            className="relative z-10 animate-[logoBreath_3.5s_ease-in-out_infinite]"
            style={{ width: 110, height: 110, objectFit: "contain" }}
          />
        </div>

        <span className="mt-6 text-xs tracking-[0.3em] uppercase text-foreground-400/50 font-medium">
          RadioFrecuenciaWeb
        </span>
      </div>
    </div>
  );
}